import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { environment } from 'environments/environment';
import { DebtCollectionReadyInvoicesDtoModel } from 'models/generated/Accounting.Api.Models.DebtCollectionReadyInvoicesDto';
import { OnHoldRequestModel } from 'models/generated/Accounting.Api.Models.OnHoldRequest';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { ReasonModel } from 'models/generated/Entities.GormCrm.Reason';
import { DebtCollectionDtoModel } from 'models/generated/Accounting.Api.Models.DebtCollectionReadyInvoicesDto';
import { firstValueFrom } from 'rxjs';
import { OnHoldReason } from 'enums/OnHoldReason';
import { BcDocumentType } from 'enums/BcDocumentType';


export class DebtCollectionDtoWithInvoiceInformation extends DebtCollectionDtoModel {
	brandId = '';
	documentNo = '';
	documentType: BcDocumentType = BcDocumentType.Unknown;
	entryNo = 0;
	ledgerHeaderId?: string;
	remainingAmountInclVat?: number;
	originalAmountInclVat?: number;
	originalDueDate?: Date;
	customerNo = '';
	postingDate?: Date;
	dueDate?: Date;
	objectionReasonId?: string;
}

@Injectable()
export class DebtCollectionService {

	private baseUrl = `${environment.gatewayUrl}/api`;
	constructor(private readonly http: HttpClient) {
	}

	async getInvoicesReadyForDebtCollection(brandId: string, searchButton: SubmitButtonComponent): Promise<DebtCollectionDtoWithInvoiceInformation[]> {
		// Start reason codes request
		const reasonsForDebtCollectionPromise = firstValueFrom(this.http.get<ReasonModel[]>(`${this.baseUrl}/reasons?includeDeleted=true`));

		// Overdue document numbers
		const overdueDocumentNos = await firstValueFrom(this.http.get<string[]>(`${this.baseUrl}/accounting/overdue-documentnos`, { params: { brandId } }));		

		// Invoices, paginated due to timeout issues with B.C. lookups
		let batch = 0;
		const batchSize = 180; // backend batches request to BC in 45 documents at a time
		let processed = 0;
		let invoicesForDebtCollection: DebtCollectionReadyInvoicesDtoModel[] = [];
		searchButton.progression = { processed: 0, total: overdueDocumentNos.length };
		while (processed < overdueDocumentNos.length) {
			const startIndex = batch * batchSize;
			const data = overdueDocumentNos.slice(startIndex, startIndex + batchSize);
			let invoices = await firstValueFrom(this.http.post<DebtCollectionReadyInvoicesDtoModel[]>(`${this.baseUrl}/accounting/invoices-ready-for-debt-collection`, data, { params: { brandId } }));
			invoicesForDebtCollection = invoicesForDebtCollection.concat(invoices);
			processed += data.length;
			searchButton.progression = { processed, total: overdueDocumentNos.length };
			batch++;
		}

		// Company statuses
		const companyIds = invoicesForDebtCollection.map(invoice => invoice.companyId);
		const [companiesInDebt, companiesForDebtCollection] = await Promise.all([
			firstValueFrom(this.http.post<string[]>(`${this.baseUrl}/accounting/companies-in-debt-collection`, companyIds)),
			firstValueFrom(this.http.post<CompanyModel[]>(`${this.baseUrl}/companies/get-companies-by-ids?includeContacts=true`, companyIds))
		]);

		// Get reason codes response
		const reasonsForDebtCollection = await reasonsForDebtCollectionPromise;

		// Generate debt collection list
		return this.generateDebtCollectionList(invoicesForDebtCollection, companiesForDebtCollection, reasonsForDebtCollection, companiesInDebt, brandId);
	}

	private generateDebtCollectionList(invoices: DebtCollectionReadyInvoicesDtoModel[], companies: CompanyModel[], reasons: ReasonModel[], companiesInDebt: string[], brandId: string): DebtCollectionDtoWithInvoiceInformation[] {
		let debtCollectionList: DebtCollectionDtoWithInvoiceInformation[] = [];
		for (let invoice of invoices) {
			var company = companies.find(c => c.id == invoice.companyId);
			if (!company) throw new Error(`Company with id ${invoice.companyId} not found`);
			var contact = company.primaryContact;
			if (!contact) throw new Error(`Primary contact for company with id ${invoice.companyId} not found`);
			var reason = reasons.find(c => c.id == invoice.objectionReasonId);

			let dto = new DebtCollectionDtoWithInvoiceInformation();
			dto.companyId = company.id;
			dto.vatNumber = company.vatNumber;
			dto.companyName = company.name;
			dto.address = company.address;
			dto.city = company.city;
			dto.zipcode = company.zipcode;
			dto.contact = contact.firstName + ' ' + contact.lastName;
			dto.emailAddress = contact.emailAddress;
			dto.businessStatus = company.businessStatus;
			dto.count = invoices.filter(i => i.companyId == company!.id).length;
			dto.inDebtCollection = companiesInDebt.indexOf(company.id!) != -1;

			dto.brandId = brandId;
			dto.documentNo = invoice.documentNo;
			dto.documentType = invoice.documentType;
			dto.entryNo = invoice.entryNo;
			dto.ledgerHeaderId = invoice.ledgerHeaderId;
			dto.remainingAmountInclVat = invoice.remainingAmountInclVat;
			dto.originalAmountInclVat = invoice.originalAmountInclVat;
			dto.originalDueDate = invoice.originalDueDate;
			dto.customerNo = invoice.customerNo;
			dto.postingDate = invoice.postingDate;
			dto.dueDate = invoice.dueDate;
			dto.objectionReasonId = invoice.objectionReasonId;
			dto.objectionReasonName = reason?.name ?? '';
			debtCollectionList.push(dto);
		}
		return debtCollectionList.sort((a, b) => { return String(a.customerNo).localeCompare(b.customerNo); });
	}

	registerInvoicesForDebtCollection(invoices: DebtCollectionDtoWithInvoiceInformation[]) {
		const onHolds = invoices.map(i => {
			const request = new OnHoldRequestModel();
			request.companyId = i.companyId;
			request.documentNo = i.documentNo;
			request.brandId = i.brandId;
			request.onHoldReason = OnHoldReason.DebtCollection;
			return request;
		});
		return firstValueFrom(this.http.post(`${this.baseUrl}/invoicing/on-holds`, onHolds));
	}
}
